import { Box, Button, Stack, Typography } from '@mui/material';
import { QrCodeVisitStatus, QrCodeVisitWeb } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { DataGridCard, DataGridCards } from 'v2/components/DataGridCards/DataGridCards';
import useModalContext from 'v2/components/Modal/useModalContext';
import { formatDate } from 'v2/utils/dateTime';
import CancelVisitModal from './CancelVisitModal';

interface Props {
  visits: QrCodeVisitWeb[];
  isLoading: boolean;
  onCancelVisit?: (visitId: string) => Promise<void>;
  showDetails?: boolean;
  variant: 'inline' | 'stacked';
}

const VisitsCards: React.FC<Props> = ({ visits, isLoading, onCancelVisit, showDetails, variant }) => {
  const { translation } = useTranslation();
  const { openModal } = useModalContext();
  const isInline = variant === 'inline';

  const handleOpenCancelModal = (visit: QrCodeVisitWeb) => {
    if (!onCancelVisit) return;
    openModal(<CancelVisitModal visitorName={visit.name} onConfirm={() => onCancelVisit(visit.id)} />);
  };

  return (
    <DataGridCards loading={isLoading} skeletonHeight={120}>
      {visits.map((visit, index) => (
        <DataGridCard key={visit.id} id={`row-${index}`}>
          <Stack spacing={1.5}>
            <Box display="flex" justifyContent="space-between" alignItems={isInline ? 'center' : 'flex-start'}>
              <Box
                display={isInline ? 'flex' : undefined}
                alignItems={isInline ? 'baseline' : undefined}
                gap={isInline ? 0.75 : undefined}
              >
                <Typography variant="body2" fontWeight="bold">
                  {formatDate(visit.datetime, 'dateOnly')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(visit.datetime, 'timeOnly')}
                </Typography>
              </Box>
              {onCancelVisit && (
                <Button variant="text" color="error" size="small" onClick={() => handleOpenCancelModal(visit)}>
                  {translation.cancel_visit}
                </Button>
              )}
            </Box>

            <Box display={isInline ? 'flex' : undefined} gap={isInline ? 2 : undefined}>
              <Box flex={isInline ? 1 : undefined}>
                <Typography variant="caption" color="text.secondary">
                  {translation.visitor}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {visit.name}
                </Typography>
                {visit.email && (
                  <Typography variant="body2" color="text.secondary">
                    {visit.email}
                  </Typography>
                )}
              </Box>

              <Box flex={isInline ? 1 : undefined}>
                <Typography variant="caption" color="text.secondary">
                  {translation.host}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {visit.host.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {visit.host.email}
                </Typography>
              </Box>
            </Box>

            {showDetails && visit.status === QrCodeVisitStatus.Used && (
              <Typography variant="body2">{translation.qr_code_used}</Typography>
            )}
            {showDetails && visit.status === QrCodeVisitStatus.Valid && (
              <Typography variant="body2">{translation.expired}</Typography>
            )}
          </Stack>
        </DataGridCard>
      ))}
    </DataGridCards>
  );
};

export default VisitsCards;
